import type { FC, ReactNode } from 'react'
import type { ViewPreset } from '@/lib/drone/control'
import type { RemoteStage } from '@/lib/remote/remote-stage'

import { useEffect, useRef } from 'react'
import { ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAppState } from '@/lib/app-store'
import { clamp, mixHex, smoothstep } from '@/lib/motion/math'
import { createMaskDriver, createStyleCache } from '@/components/landing/primitives'
import { controller } from '@/constants/content'

import {
    emptyMetrics,
    enterProgress,
    isNear,
    measureSection,
    useEngine
} from '@/lib/motion/section'

import {
    getControl,
    live,
    markInput,
    requestView,
    returnToPath,
    setControl,
    shiftSpeed,
    takeControl
} from '@/lib/drone/control'

const INK = '#1d2a31'
const PALE = '#c6cdd0'

const MOVEMENT_CODES = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'])

const VIEW_KEYS: Record<string, ViewPreset> = {
    Digit1: 'side',
    Digit2: 'front',
    Digit3: 'top',
    Digit4: 'quarter'
}

const READOUTS = [
    { id: 'heading', label: 'Heading', short: 'Hdg', initial: '000°' },
    { id: 'pitch', label: 'Pitch', short: 'Pit', initial: ' 00°' },
    { id: 'roll', label: 'Roll', short: 'Rol', initial: ' 00°' },
    { id: 'speed', label: 'Speed', short: 'Spd', initial: '0.0' }
]

/* Sonar ring tick marks, rounded so server and browser agree */
const RING_TICKS = Array.from({ length: 72 }, (_, index) => {
    const angle = (index / 72) * Math.PI * 2
    const major = index % 6 === 0
    const inner = major ? 0.9 : 0.94
    const round = (value: number): number => Number(value.toFixed(2))

    return {
        x1: round(Math.cos(angle) * 96 * inner),
        y1: round(Math.sin(angle) * 26 * inner),
        x2: round(Math.cos(angle) * 96),
        y2: round(Math.sin(angle) * 26),
        major
    }
})

interface Flake {
    x: number
    y: number
    z: number
    phase: number
    tint: boolean
}

const createFlakes = (count: number): Array<Flake> => {
    let seed = 7

    const random = (): number => {
        seed = (seed * 16807) % 2147483647

        return (seed - 1) / 2147483646
    }

    return Array.from({ length: count }, (_, index) => ({
        x: random(),
        y: random(),
        z: 0.25 + random() * 0.75,
        phase: random() * Math.PI * 2,
        tint: index % 6 === 0
    }))
}

const isTyping = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false

    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

const axis = (value: number): number => clamp(value, -1, 1)

const deadzone = (value: number): number => (Math.abs(value) < 0.14 ? 0 : (value - Math.sign(value) * 0.14) / 0.86)

const signed = (value: number): string => `${value < -0.5 ? '-' : value > 0.5 ? '+' : ' '}${String(Math.min(99, Math.abs(Math.round(value)))).padStart(2, '0')}`

interface BraceProps {
    children: ReactNode
    className?: string
}

/** Controller labels sit in braces, like { PILOT MODE }. */
const Brace: FC<BraceProps> = ({ children, className }): ReactNode => {
    return (
        <span className={cn('label-mono inline-flex items-center gap-[0.6em] whitespace-nowrap', className)}>
            <span aria-hidden='true'>{'{'}</span>
            <span>{children}</span>
            <span aria-hidden='true'>{'}'}</span>
        </span>
    )
}

const ControllerSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const ruleRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const snowRef = useRef<HTMLCanvasElement>(null)
    const sweepRef = useRef<SVGLineElement>(null)
    const remoteCanvasRef = useRef<HTMLCanvasElement>(null)
    const proxyRef = useRef<HTMLDivElement>(null)
    const hintRef = useRef<HTMLParagraphElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const valueRefs = useRef<Array<HTMLSpanElement | null>>([])
    const shortRefs = useRef<Array<HTMLSpanElement | null>>([])
    const meterRefs = useRef<Array<HTMLSpanElement | null>>([])
    const stageRef = useRef<RemoteStage | null>(null)
    const disposedRef = useRef(false)

    /* Release the remote when the page goes away ------------------------- */

    useEffect(() => {
        disposedRef.current = false

        return () => {
            disposedRef.current = true
            stageRef.current?.dispose()
            stageRef.current = null
        }
    }, [])

    /* Keyboard, only while the vehicle is free to fly here --------------- */

    useEffect(() => {
        const pressed = new Set<string>()

        const recompute = (): void => {
            const has = (...codes: Array<string>): number => (codes.some(code => pressed.has(code)) ? 1 : 0)

            live.keyLift = has('KeyW') - has('KeyS')
            live.keyTurn = has('KeyD') - has('KeyA')
            live.keyPitch = has('ArrowUp') - has('ArrowDown')
            live.keyRoll = has('ArrowRight') - has('ArrowLeft')
            live.keyTriggerL = has('KeyQ')
            live.keyTriggerR = has('KeyE')
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.metaKey || event.ctrlKey || event.altKey) return
            if (isTyping(event.target)) return

            const app = getAppState()

            if (app.menuOpen || app.requestOpen) return
            if (getControl().zone !== 'controller') return

            const code = event.code
            const stage = stageRef.current

            if (MOVEMENT_CODES.has(code)) {
                if (code.startsWith('Arrow')) event.preventDefault()

                pressed.add(code)
                recompute()
                takeControl()

                return
            }

            if (event.repeat) return

            if (code === 'KeyQ' || code === 'KeyE') {
                pressed.add(code)
                recompute()
                shiftSpeed(code === 'KeyQ' ? -1 : 1)
                stage?.flash(code === 'KeyQ' ? 'triggerL' : 'triggerR')

                return
            }

            if (code === 'KeyR') {
                returnToPath()
                stage?.flash('path')

                return
            }

            const view = VIEW_KEYS[code]

            if (view) {
                requestView(view)
                stage?.flash('view')
            }
        }

        const onKeyUp = (event: KeyboardEvent): void => {
            if (!pressed.delete(event.code)) return

            recompute()
        }

        const onBlur = (): void => {
            pressed.clear()
            recompute()
        }

        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('keyup', onKeyUp)
        window.addEventListener('blur', onBlur)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('keyup', onKeyUp)
            window.removeEventListener('blur', onBlur)
            pressed.clear()
            recompute()
        }
    }, [])

    /* Gamepad presence ------------------------------------------------------ */

    useEffect(() => {
        const onConnect = (): void => setControl({ pad: true })

        const onDisconnect = (): void => {
            const pads = navigator.getGamepads ? Array.from(navigator.getGamepads()).filter(Boolean) : []

            if (pads.length > 0) return

            setControl({ pad: false })
            live.gpL.x = 0
            live.gpL.y = 0
            live.gpR.x = 0
            live.gpR.y = 0
            live.gpTriggerL = 0
            live.gpTriggerR = 0
        }

        window.addEventListener('gamepadconnected', onConnect)
        window.addEventListener('gamepaddisconnected', onDisconnect)

        return () => {
            window.removeEventListener('gamepadconnected', onConnect)
            window.removeEventListener('gamepaddisconnected', onDisconnect)
        }
    }, [])

    /* Scroll-driven scene, input blending, telemetry ----------------------- */

    useEngine(() => {
        const set = createStyleCache()
        const mask = createMaskDriver(titleRef.current)
        const flakes = createFlakes(34)
        const smooth = { lx: 0, ly: 0, rx: 0, ry: 0 }
        const snow = snowRef.current
        const snowContext = snow ? snow.getContext('2d') : null

        let metrics = emptyMetrics()
        let snowWidth = 0
        let snowHeight = 0
        let snowRatio = 1
        let loading = false
        let textClock = 0
        let zoneSince = -100
        let pilotTime = 0
        let wasInZone = false
        let previousLeft = 0
        let previousRight = 0
        let lastSweep = ''

        const createRemote = (): void => {
            const canvas = remoteCanvasRef.current
            const proxy = proxyRef.current

            if (loading || !canvas || !proxy) return

            loading = true

            import('@/lib/remote/remote-stage')
                .then(({ RemoteStage: Stage }) => {
                    if (disposedRef.current) return

                    try {
                        stageRef.current = new Stage(canvas, proxy)
                    }
                    catch (error) {
                        console.error('[orvane] remote controller could not start', error)
                    }
                })
                .catch((error: unknown) => console.error('[orvane] remote controller failed to load', error))
        }

        const drawSnow = (dt: number, time: number): void => {
            if (!snowContext || snowWidth === 0) return

            snowContext.setTransform(snowRatio, 0, 0, snowRatio, 0, 0)
            snowContext.clearRect(0, 0, snowWidth, snowHeight)

            for (const flake of flakes) {
                flake.x -= (0.004 + flake.z * 0.01) * dt
                flake.y -= (0.002 + flake.z * 0.004) * dt

                if (flake.x < -0.02) flake.x += 1.04
                if (flake.y < -0.02) flake.y += 1.04

                const x = (flake.x + Math.sin(time * 0.4 + flake.phase) * 0.006) * snowWidth
                const y = (flake.y + Math.cos(time * 0.3 + flake.phase) * 0.008) * snowHeight
                const radius = 0.5 + flake.z * 1.3

                snowContext.fillStyle = flake.tint
                    ? `rgba(95, 191, 208, ${(0.18 + flake.z * 0.3).toFixed(3)})`
                    : `rgba(29, 42, 49, ${(0.06 + flake.z * 0.16).toFixed(3)})`
                snowContext.beginPath()
                snowContext.arc(x, y, radius, 0, Math.PI * 2)
                snowContext.fill()
            }
        }

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)

                if (snow) {
                    snowWidth = snow.clientWidth
                    snowHeight = snow.clientHeight
                    snowRatio = Math.min(window.devicePixelRatio || 1, 1.5)
                    snow.width = Math.max(1, Math.round(snowWidth * snowRatio))
                    snow.height = Math.max(1, Math.round(snowHeight * snowRatio))
                }

                stageRef.current?.measure()
            },
            frame: (state) => {
                const control = getControl()
                const dt = state.dt

                /* A real pad drives the 3D sticks directly */

                if (control.pad && navigator.getGamepads) {
                    const pad = Array.from(navigator.getGamepads()).find(Boolean)

                    if (pad) {
                        live.gpL.x = deadzone(pad.axes[0] ?? 0)
                        live.gpL.y = -deadzone(pad.axes[1] ?? 0)
                        live.gpR.x = deadzone(pad.axes[2] ?? 0)
                        live.gpR.y = -deadzone(pad.axes[3] ?? 0)

                        const left = pad.buttons[6]?.value ?? 0
                        const right = pad.buttons[7]?.value ?? 0

                        live.gpTriggerL = left
                        live.gpTriggerR = right

                        if (control.zone === 'controller') {
                            if (left > 0.6 && previousLeft <= 0.6) shiftSpeed(-1)
                            if (right > 0.6 && previousRight <= 0.6) shiftSpeed(1)
                        }

                        previousLeft = left
                        previousRight = right

                        const magnitude = Math.max(Math.abs(live.gpL.x), Math.abs(live.gpL.y), Math.abs(live.gpR.x), Math.abs(live.gpR.y))

                        if (magnitude > 0.35) takeControl()
                    }
                }

                /* Blend sticks, keys and pad; keys ease the sticks over */

                const follow = 1 - Math.exp(-12 * dt)

                smooth.lx += (live.keyTurn - smooth.lx) * follow
                smooth.ly += (live.keyLift - smooth.ly) * follow
                smooth.rx += (live.keyRoll - smooth.rx) * follow
                smooth.ry += (live.keyPitch - smooth.ry) * follow

                live.stickL.x = axis(live.dragL.x + smooth.lx + live.gpL.x)
                live.stickL.y = axis(live.dragL.y + smooth.ly + live.gpL.y)
                live.stickR.x = axis(live.dragR.x + smooth.rx + live.gpR.x)
                live.stickR.y = axis(live.dragR.y + smooth.ry + live.gpR.y)

                const deflection = Math.abs(live.stickL.x) + Math.abs(live.stickL.y) + Math.abs(live.stickR.x) + Math.abs(live.stickR.y)

                if (deflection > 0.05 && control.mode === 'manual') markInput()

                if (!isNear(metrics, state.scroll, state.vh)) return

                if (!stageRef.current) createRemote()

                /* Entry: title rises, rule draws, remote lifts into place ---- */

                const enter = enterProgress(metrics, state.scroll, state.vh)

                mask(smoothstep(0.3, 1, enter))
                set(ruleRef.current, 'transform', `scaleX(${smoothstep(0.4, 1, enter).toFixed(4)})`)
                set(bodyRef.current, 'opacity', smoothstep(0.5, 0.95, enter).toFixed(3))

                const stage = stageRef.current

                if (stage) {
                    stage.setAppear(smoothstep(0.62, 1, enter))
                    stage.frame(state)
                }

                /* Hint line on arrival, scroll prompt after some flying ---- */

                const inZone = control.zone === 'controller'

                if (inZone && !wasInZone) {
                    zoneSince = state.time
                    pilotTime = 0
                }

                wasInZone = inZone

                if (inZone && control.mode === 'manual') pilotTime += dt

                set(hintRef.current, 'opacity', inZone && state.time - zoneSince < 5.5 ? '1' : '0')
                set(scrollRef.current, 'opacity', inZone && pilotTime > 3.5 ? '1' : '0')

                /* Telemetry ticks like a flight display ---------------------- */

                textClock += dt

                if (textClock > 0.08) {
                    textClock = 0

                    const telemetry = live.telemetry
                    const heading = `${String(Math.round(((telemetry.heading % 360) + 360) % 360) % 360).padStart(3, '0')}°`
                    const values = [heading, `${signed(telemetry.pitch)}°`, `${signed(telemetry.roll)}°`, Math.min(99.9, telemetry.speed).toFixed(1)]
                    const meters = [
                        (((telemetry.heading % 360) + 360) % 360) / 360,
                        Math.min(1, Math.abs(telemetry.pitch) / 30),
                        Math.min(1, Math.abs(telemetry.roll) / 30),
                        Math.min(1, telemetry.speed / 8)
                    ]

                    values.forEach((value, index) => {
                        const full = valueRefs.current[index]
                        const short = shortRefs.current[index]

                        if (full && full.textContent !== value) full.textContent = value
                        if (short && short.textContent !== value) short.textContent = value

                        set(meterRefs.current[index], 'transform', `scaleX(${meters[index].toFixed(3)})`)
                    })
                }

                /* Sonar ring sweep and a few slow dust motes ----------------- */

                const angle = state.time * 0.9
                const sweep = `${(Math.cos(angle) * 96).toFixed(2)},${(Math.sin(angle) * 26).toFixed(2)}`

                if (sweep !== lastSweep && sweepRef.current) {
                    lastSweep = sweep

                    const [x, y] = sweep.split(',')

                    sweepRef.current.setAttribute('x2', x)
                    sweepRef.current.setAttribute('y2', y)
                }

                drawSnow(state.reducedMotion ? 0 : dt, state.time)
            }
        }
    })

    const telemetryBox = (readout: typeof READOUTS[number], index: number): ReactNode => (
        <div key={readout.id} className='relative overflow-hidden border border-line bg-white/45 px-3 py-2.5 backdrop-blur-[2px]'>
            <span className='label-mono whitespace-pre tabular-nums text-ink'>
                {'{ '}
                <span className='text-haze'>{readout.label}</span>
                {' '}
                <span ref={(element) => { valueRefs.current[index] = element }}>{readout.initial}</span>
                {' }'}
            </span>

            <span aria-hidden='true' className='absolute inset-x-0 bottom-0 h-px bg-line'>
                <span
                    ref={(element) => { meterRefs.current[index] = element }}
                    className='absolute inset-y-0 left-0 block w-full origin-left bg-glacier'
                    style={{ transform: 'scaleX(0)' }}
                />
            </span>
        </div>
    )

    return (
        <section id='controller' ref={sectionRef} aria-labelledby='controller-title' className='relative h-[400vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 flex h-svh flex-col overflow-hidden px-4 pt-[68px] md:px-10 md:pt-[72px]'>
                <h2 id='controller-title' ref={titleRef} aria-label={controller.title} className='display-giant shrink-0 text-[clamp(52px,min(13vw,17vh),220px)]'>
                    <span className='block overflow-hidden pb-[0.06em]'>
                        <span data-mask-inner='' aria-hidden='true' className='mask-inner block will-change-transform'>
                            {controller.title.split('').map((letter, index) => (
                                <span key={`${letter}-${index}`} style={{ color: index > 5 ? mixHex(INK, PALE, (index - 5) / 4) : INK }}>
                                    {letter}
                                </span>
                            ))}
                        </span>
                    </span>
                </h2>

                <div className='mt-1 grid shrink-0 grid-cols-4 gap-1.5 md:hidden'>
                    {READOUTS.map((readout, index) => (
                        <div key={readout.id} className='border border-line bg-white/45 px-1.5 py-1.5 text-center'>
                            <span className='label-mono whitespace-pre tabular-nums text-[9.5px] text-ink'>
                                <span className='text-haze'>{readout.short}</span>
                                {' '}
                                <span ref={(element) => { shortRefs.current[index] = element }}>{readout.initial}</span>
                            </span>
                        </div>
                    ))}
                </div>

                <div ref={ruleRef} aria-hidden='true' className='mt-3 h-px w-full shrink-0 origin-left bg-line md:mt-4' />

                <div ref={bodyRef} className='relative grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[minmax(0,1fr)_minmax(0,2.1fr)_minmax(0,0.8fr)] md:grid-rows-1 md:gap-8'>
                    <div className='flex flex-col gap-2 pt-4 md:justify-between md:pb-8 md:pt-7'>
                        <div className='flex flex-col gap-2 md:gap-4'>
                            <Brace className='text-haze'>{controller.label}</Brace>

                            <p className='max-w-[360px] text-[clamp(20px,2vw,34px)] font-light leading-[1.08] tracking-[-0.035em] text-ink'>
                                {controller.statement}
                            </p>

                            <p className='hidden max-w-[300px] text-[15px] leading-[1.5] tracking-[-0.01em] text-haze md:block'>
                                {controller.follow}
                            </p>
                        </div>

                        <ul className='hidden flex-col gap-2.5 md:flex'>
                            {controller.hints.map(hint => (
                                <li key={hint}>
                                    <Brace className='text-ink/70'>{hint}</Brace>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='relative flex min-h-0 flex-col'>
                        <div data-anchor='controller-stage' className='relative min-h-[120px] flex-1'>
                            <canvas ref={snowRef} aria-hidden='true' className='pointer-events-none absolute inset-0 block h-full w-full' />

                            <svg
                                aria-hidden='true'
                                viewBox='-100 -30 200 60'
                                className='pointer-events-none absolute bottom-[2%] left-1/2 w-[min(88%,560px)] -translate-x-1/2 overflow-visible'
                            >
                                <ellipse rx='96' ry='26' fill='none' stroke={INK} strokeOpacity='0.16' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                                <ellipse rx='64' ry='17.3' fill='none' stroke={INK} strokeOpacity='0.1' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                                <ellipse rx='32' ry='8.7' fill='none' stroke={INK} strokeOpacity='0.08' strokeWidth='1' vectorEffect='non-scaling-stroke' />

                                {RING_TICKS.map((tick, index) => (
                                    <line
                                        key={index}
                                        x1={tick.x1}
                                        y1={tick.y1}
                                        x2={tick.x2}
                                        y2={tick.y2}
                                        stroke={INK}
                                        strokeOpacity={tick.major ? 0.4 : 0.18}
                                        strokeWidth='1'
                                        vectorEffect='non-scaling-stroke'
                                    />
                                ))}

                                <line ref={sweepRef} x1='0' y1='0' x2='96' y2='0' stroke='#5fbfd0' strokeOpacity='0.75' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                                <circle r='1.1' fill={INK} fillOpacity='0.5' />
                            </svg>
                        </div>

                        <div className='relative h-[30svh] shrink-0 md:h-[36svh]'>
                            <canvas ref={remoteCanvasRef} aria-hidden='true' className='pointer-events-none absolute inset-0 block h-full w-full' />

                            <div
                                ref={proxyRef}
                                role='group'
                                aria-label='3D remote controller. Drag the sticks to fly the drone.'
                                data-cursor='hover'
                                className='absolute left-0 top-0 touch-none select-none'
                                style={{ width: 0, height: 0 }}
                            />

                            <p
                                ref={hintRef}
                                aria-hidden='true'
                                className='label-mono pointer-events-none absolute inset-x-0 bottom-1 hidden text-center text-haze opacity-0 transition-opacity duration-1000 md:block'
                            >
                                {controller.keys}
                            </p>
                        </div>
                    </div>

                    <div className='hidden flex-col gap-2 pt-7 md:flex'>
                        {READOUTS.map(telemetryBox)}
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    aria-hidden='true'
                    className='pointer-events-none absolute bottom-4 right-4 flex items-center gap-2 text-ink opacity-0 transition-opacity duration-1000 md:bottom-6 md:right-10'
                >
                    <Brace>{controller.scroll}</Brace>
                    <ArrowDown className='scroll-nudge size-3.5' />
                </div>
            </div>
        </section>
    )
}

export default ControllerSection
